/**
 * What the signed-in operator may do.
 *
 * The server reports this on the session probe, already expanded: an admin
 * holding PERMISSION_ALL comes back holding everything it implies, so nothing
 * here has to know that the permissions form a hierarchy. The list is a flat
 * set of names, and the only question asked of it is whether a name is in it.
 *
 * None of this is enforcement. It decides which controls are drawn, so a
 * planner is not offered a Delete button that fails when pressed; the server
 * checks every call again through its authorization interceptor
 * (backend/internal/authz), and would refuse those calls whatever this file
 * said. Mirrors famtrak's features/authorization, minus the group scoping that
 * this application has no groups for.
 */
import { Permission, PermissionSchema } from '../../proto/travplaces/v1/authz_pb';

export { Permission };

/**
 * The enum value → the name the server sends.
 *
 * protoc-gen-es strips the `PERMISSION_` prefix from the TypeScript enum
 * (`Permission.CITY_WRITE`), while the API speaks the full proto name
 * (`PERMISSION_CITY_WRITE`) because that is what is stored in the database.
 * The descriptor carries both, so the mapping is read from the generated
 * schema rather than rebuilt by hand — a hand-written table would be one more
 * thing to forget to update.
 */
const PROTO_NAME = new Map<number, string>(
  PermissionSchema.values.map((value) => [value.number, value.name]),
);

/** The set of permissions an operator holds, as a lookup. */
export type PermissionSet = ReadonlySet<string>;

/** Builds the lookup from what the session endpoint returned. */
export function permissionSet(permissions: readonly string[] | undefined): PermissionSet {
  return new Set(permissions ?? []);
}

/**
 * Whether the set covers any of the given permissions.
 *
 * "Any", not "all", because that is how a UI question is usually phrased —
 * show this panel if the operator can do any of the things on it. Pass a
 * single permission for the common case.
 */
export function hasPermission(
  held: PermissionSet,
  permission: Permission | readonly Permission[],
): boolean {
  // `typeof === 'number'` rather than Array.isArray: the enum is numeric, so
  // this narrows the union cleanly, while Array.isArray widens a readonly
  // array to any[] and loses the element type.
  const wanted: readonly Permission[] = typeof permission === 'number' ? [permission] : permission;

  return wanted.some((p) => {
    const name = PROTO_NAME.get(p);

    // A permission the descriptor does not know is not a permission anybody
    // holds. Denying is the safe direction, and the mismatch would mean the
    // generated code is out of step with itself.
    return name !== undefined && held.has(name);
  });
}
